<template>
  <ModalOverlay
    v-model="isOpened"
    :no-close-backdrop="true"
    :background-class="'b-pop-up__holder b-pop-up--otp'"
    @close="onClose">
    <template #header>
      <div class="b-pop-up__header__title">
        {{ dictionary.modalTitle }}
      </div>
    </template>
    <template #body>
      <Loader v-if="isLoading" />

      <div class="b-pop-up__content">
        <div class="b-pop-up--otp__top">
          <div class="b-pop-up--otp__title">
            {{ dictionary.bodyTitle }}
          </div>
          <div class="b-pop-up--otp__txt" v-html="bodyText"></div>
        </div>

        <Alert
          v-if="showAlert"
          :messages="[dictionary.otpWrongCode]"
          :dismissible="false"></Alert>

        <form
          class="b-pop-up--otp__form"
          autocomplete="off"
          @submit.prevent="onSubmitForm">
          <div class="form-floating">
            <input
              id="otpcode"
              v-model.trim="otpcode"
              type="text"
              class="form-control"
              :placeholder="dictionary.otpLabel"
              :class="{
                'form-control--error': v$.otpcode.$error,
              }"
              maxlength="6"
              @blur="v$.otpcode.$touch" />
            <label for="otpcode">{{ dictionary.otpLabel }}</label>

            <div v-if="!canRevalidate" class="field-validation-error">
              {{ dictionary.maxAllowedRevalidateTriesExceeded }}
            </div>

            <template v-if="v$.otpcode.$error">
              <div
                v-if="v$.otpcode.required.$invalid"
                class="field-validation-error">
                {{ dictionary.otpRequired }}
              </div>
              <div
                v-if="v$.otpcode.regex.$invalid"
                class="field-validation-error">
                {{ dictionary.otpRegex }}
              </div>
            </template>
          </div>

          <div class="b-pop-up__cta-area">
            <button
              type="submit"
              class="b-pop-up__cta-login btn--block"
              :disabled="submitButtonIsDisabled">
              <span>{{ dictionary.ctaSubmitText }}</span>
            </button>
          </div>
        </form>

        <Resend
          v-if="canResend"
          ref="resend"
          :dictionary="dictionary"
          :handler="configuration.requestOtpHandler"
          :email="email"
          :time-out="configuration.countDuration"
          :max-resends="configuration.maxResends"
          @loading="setLoading"
          @resend="onResend" />

        <div v-else class="field-validation-error">
          {{ dictionary.maxAllowedResendTriesExceeded }}
        </div>
      </div>
    </template>
  </ModalOverlay>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'

import { scrollToValidation } from './../../../lib/lib'
import constants from './../../../lib/constants'
import getErrorMessage from '../../../lib/getErrorMessage'
import postRequestHandler from './../../../lib/postRequestHandler'
import { useBookingStore } from './../../store/BookingStore'

import Loader from '../Shared/Loader.vue'
import Resend from './Resend.vue'
import ModalOverlay from '../ModalOverlay.vue'
import Alert from '../Shared/Alert.vue'

const { otp } = constants.regex

export default {
  components: {
    Alert,
    Loader,
    ModalOverlay,
    Resend,
  },
  props: {
    dictionary: {
      default() {
        return {}
      },
      type: Object,
    },
    configuration: {
      default() {
        return {}
      },
      type: Object,
    },
    email: {
      default: '',
      type: String,
    },
    canResend: {
      default: true,
      type: Boolean,
    },
    canRevalidate: {
      default: true,
      type: Boolean,
    },
  },
  emits: [
    'resend',
    'resendError',
    'revalidateError',
    'succeess',
    'otpBlockedError',
    'close-modal',
  ],
  setup() {
    return { store: useBookingStore(), v$: useVuelidate() }
  },
  data() {
    return {
      captchaReady: false,
      errorMsgs: [],
      isLoading: false,
      otpcode: '',
      isOpened: true,
      otpCheckError: false,
    }
  },
  validations() {
    return {
      otpcode: {
        required,
        regex: helpers.regex(otp),
      },
    }
  },
  computed: {
    showAlert() {
      if (this.otpCheckError && this.canRevalidate) {
        return true
      }

      if (!this.isLoading && this.errorMsgs.length) {
        return true
      }

      return false
    },

    submitButtonIsDisabled() {
      return this.isLoading || this.v$.$invalid || !this.canRevalidate
    },

    bodyText() {
      return this.dictionary.bodyDescription.replace(
        '{email}',
        `<em>${this.email}</em>`
      )
    },
  },
  methods: {
    onResend(counter) {
      this.removeErrors()
      this.otpcode = ''
      this.v$.$reset()
      this.otpCheckError = false
      this.$emit('resend', counter)
    },

    setLoading(bool) {
      this.isLoading = bool
    },

    async onSubmitForm() {
      this.v$.$validate()

      if (this.isLoading) {
        return false
      }

      if (this.v$.$invalid) {
        this.$nextTick(() => {
          scrollToValidation()
        })
        return false
      }
      console.log('show loader')
      this.isLoading = true
      this.removeErrors()
      this.otpCheckError = false

      const postData = {
        email: this.email,
        otpcode: this.otpcode,
        userSessionId: this.store.bookingProgress.usersessionId,
      }

      await postRequestHandler({
        postData,
        endpoint: this.configuration.validateOtpHandler,
        successCallback: this.verificationOtpSuccess,
        errorCallback: this.verificationOtpError,
        refreshTokenHandler: this.configuration.refresh,
        captchaAction: this.configuration.validateOtpCaptchaAction,
      })
    },

    verificationOtpSuccess(data) {
      this.isLoading = false
      if (data.data.verificationCode) {
        this.$emit('succeess', data.data.verificationCode)
      } else {
        this.otpCheckError = true

        const { canResend, canRevalidate } = data.data
        if (!canResend && !canRevalidate) {
          this.$emit('otpBlockedError')
          return
        }
        if (!canRevalidate) {
          this.$emit('revalidateError')
          return
        }
      }
    },

    verificationOtpError(messages) {
      this.isLoading = false
      this.errorMsgs = getErrorMessage(
        messages,
        this.dictionary.defaultErrorMessage
      )
    },

    removeErrors() {
      this.errorMsgs = []
    },

    onClose() {
      this.$emit('close-modal')
    },
  },
}
</script>
